import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import { fromSlimRow, refreshPracticalInfo, toEvent } from './to-event';
import { VENUE_PROFILES, addProfiles, type VenueProfile } from './venues';
import type { Event, RawEvent } from '../types';

/**
 * The catalog, delivered lazily.
 *
 * The prerender has every event (src/data/live.ts). The browser has none at
 * boot — the bundle no longer carries the catalog — except what the page it
 * loaded embedded for itself (`window.__DATA__`, written by
 * scripts/prerender.mjs from what the render touched). Anything else comes
 * from static slices:
 *
 *   /data/home.json        full rows the homepage can draw
 *   /data/index.json       slim rows, everything — filters, search fallback, maps
 *   /data/cat/<name>.json  slim rows for one category — listing pages
 *   /data/e/<hh>.json      full rows in 256 id-hash buckets — one event page
 *
 * A page declares a *page key* and the slice that backs it. The prerender
 * marks the key satisfied (its rows are in the HTML), so a landing page
 * fetches nothing; a page reached by navigation fetches its backing slice
 * once. Full rows always win over slim ones.
 */
export type SliceKey = 'home' | 'index' | `cat:${string}` | `bucket:${string}`;
export type Scope = { page: string; backing: SliceKey };

const SSR = import.meta.env.SSR;

export const bucketOf = (id: string) => {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (h % 256).toString(16).padStart(2, '0');
};

/** Ids the catalog buckets never carry: they live only in the edge API (functions/api/e/[id].ts). */
export const EDGE_ID = /^(tn|ht)-/;

type SlimRow = Parameters<typeof fromSlimRow>[0];

class CatalogStore {
  private byId = new Map<string, Event>();
  private loaded = new Set<string>();
  private pages = new Set<string>();
  private inflight = new Map<string, Promise<void>>();
  private listeners = new Set<() => void>();
  private sorted: Event[] | null = null;
  version = 0;

  /** Notify subscribers without changing rows (derived data arrived). */
  touch() {
    this.bump();
  }

  /** Re-derive practical info for full rows after venue facts arrive. */
  refreshFacts() {
    for (const [id, e] of this.byId) if (!e.partial) this.byId.set(id, refreshPracticalInfo(e));
    this.bump();
  }

  /** Edge renderer only (src/entry-edge.tsx): forget every row, so one
   *  request's events can never leak into the next request's page. */
  reset() {
    this.byId.clear();
    this.loaded.clear();
    this.pages.clear();
    this.inflight.clear();
    this.bump();
  }

  /** Everything at once — the prerender, and tests. */
  seed(raws: RawEvent[]) {
    for (const r of raws) this.byId.set(r.id, toEvent(r));
    this.loaded.add('home');
    this.loaded.add('index');
    for (const e of this.byId.values()) this.loaded.add(`cat:${e.category.toLowerCase()}`);
    for (let i = 0; i < 256; i++) this.loaded.add(`bucket:${i.toString(16).padStart(2, '0')}`);
    this.bump();
  }

  add(events: Event[]) {
    let changed = false;
    for (const e of events) {
      const have = this.byId.get(e.id);
      if (have && !have.partial) continue; // full beats slim; never downgrade
      if (have?.partial && e.partial) continue;
      this.byId.set(e.id, e);
      changed = true;
    }
    if (changed) this.bump();
  }

  /**
   * A tap on a card whose row the store does not hold (the homepage's rows come with the page, not
   * from a slice): the event page then finds this slim row and draws its hero and the board's frame
   * at once instead of an empty busy block, while the full row loads. Never downgrades a row it has.
   */
  prime(e: Event) {
    this.add([e.partial ? e : { ...e, partial: true }]);
  }

  private bump() {
    this.sorted = null;
    this.version++;
    for (const fn of this.listeners) fn();
  }

  get(id: string) {
    return this.byId.get(id);
  }

  /** All known events, soonest first. Cached until the store changes. */
  all(): Event[] {
    if (!this.sorted) this.sorted = [...this.byId.values()].sort((a, b) => a.date.localeCompare(b.date));
    return this.sorted;
  }

  has(key: SliceKey) {
    return this.loaded.has(key);
  }

  pageReady(page: string) {
    return SSR || this.pages.has(page);
  }

  markPages(pages: string[]) {
    for (const p of pages) this.pages.add(p);
  }

  /** Fetch a slice once. Never throws: a failed fetch leaves the store as it
   *  was and the page shows what it has. */
  ensure(key: SliceKey): Promise<void> {
    if (SSR || this.loaded.has(key)) return Promise.resolve();
    const running = this.inflight.get(key);
    if (running) return running;
    const url =
      key === 'home'
        ? '/data/home.json'
        : key === 'index'
          ? '/data/index.json'
          : key.startsWith('cat:')
            ? `/data/cat/${key.slice(4)}.json`
            : `/data/e/${key.slice(7)}.json`;
    const p = fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${url}: ${r.status}`))))
      .then((body) => {
        if (Array.isArray(body)) this.add((body as RawEvent[]).map(toEvent));
        else this.add((body.rows as SlimRow[]).map(fromSlimRow));
        this.loaded.add(key);
        this.bump();
      })
      .catch((err) => {
        if (typeof console !== 'undefined') console.warn('catalog slice failed:', err?.message ?? err);
      })
      .finally(() => this.inflight.delete(key));
    this.inflight.set(key, p);
    return p;
  }

  private settledIds = new Set<string>();

  /** True once ensureEvent has looked everywhere for this id (found or not). */
  settled(id: string) {
    return this.settledIds.has(id);
  }

  /** The edge API's row for one event (long-tail and announcement events live only in D1,
   *  functions/api/e/[id].ts). Never throws. */
  private fetchEdgeRow(id: string): Promise<void> {
    return fetch(`/api/e/${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((raw) => {
        if (raw && (raw as RawEvent).id === id) this.add([toEvent(raw as RawEvent)]);
      })
      .catch(() => undefined);
  }

  /** One event's full row: its id bucket (the prerendered catalog), else the
   *  edge API. Ids the catalog never carries (EDGE_ID: Ticket Network and HelloTickets
   *  long-tail events) ask both at once and the first full row wins, instead of waiting
   *  for the bucket to say "not here". Never throws; fetches each id once. */
  ensureEvent(id: string): Promise<void> {
    if (SSR) return Promise.resolve();
    const have = this.byId.get(id);
    if (have && !have.partial) return Promise.resolve();
    const key = `event:${id}`;
    const running = this.inflight.get(key);
    if (running) return running;
    const bucket = this.ensure(`bucket:${bucketOf(id)}`);
    const found = () => {
      const now = this.byId.get(id);
      return !!now && !now.partial;
    };
    const looked: Promise<void> = EDGE_ID.test(id)
      ? new Promise<void>((resolve) => {
          let left = 2;
          const one = () => {
            if (found() || --left === 0) resolve();
          };
          void bucket.then(one);
          void this.fetchEdgeRow(id).then(one);
        })
      : bucket.then(() => (found() ? undefined : this.fetchEdgeRow(id)));
    const p = looked.finally(() => {
      this.inflight.delete(key);
      this.settledIds.add(id);
      this.bump();
    });
    this.inflight.set(key, p);
    return p;
  }

  /** A page's data: nothing to do if the prerender embedded it, else the
   *  backing slice once. */
  ensurePage({ page, backing }: Scope): Promise<void> {
    if (this.pageReady(page)) return Promise.resolve();
    return this.ensure(backing).then(() => {
      this.pages.add(page);
      this.bump();
    });
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
}

export const store = new CatalogStore();

/**
 * During prerender, AppShell provides a collector; the hooks below record
 * the rows they handed to the page — 'full' for rendered rows, 'slim' for
 * rows a page only counted or filtered — plus the page keys they declared.
 * The prerender embeds all of it so the browser's first render matches.
 */
/** Embed levels: 'event' keeps practicalInfo (the event page's own row),
 *  'full' is a card's row without it, 'slim' is the compact array form. */
export type Level = 'event' | 'full' | 'slim';
const RANK: Record<Level, number> = { slim: 0, full: 1, event: 2 };
export type Collector = {
  rows: Map<string, Level>;
  pages: Set<string>;
  venues: Set<string>;
  /** derived datasets a page used (A–Z directories, …), by key */
  extra: Map<string, unknown>;
};
export const newCollector = (): Collector => ({ rows: new Map(), pages: new Set(), venues: new Set(), extra: new Map() });
export const CollectorContext = createContext<Collector | null>(null);

const collect = (c: Collector | null, list: Event[], as: Level) => {
  if (!c) return;
  for (const e of list) {
    const have = c.rows.get(e.id);
    if (!have || RANK[as] > RANK[have]) c.rows.set(e.id, as);
  }
};

/** Record rows the page renders (full) or merely enumerates (slim). */
export const useCollect = (list: Event[], as: Level = 'full') => {
  collect(useContext(CollectorContext), list, as);
};

const extras = new Map<string, unknown>();
const extraInflight = new Map<string, Promise<void>>();

/**
 * A derived dataset by key: computed from the full catalog in the prerender
 * (and embedded with the page), fetched from /data/<key>.json in the browser
 * when the page didn't carry it. `produce` must be pure over the catalog.
 * An empty key switches the hook off (returns null, embeds and fetches nothing),
 * for components that need the data only sometimes.
 */
export const useExtra = <T,>(key: string, produce: () => T): T | null => {
  const version = useSyncExternalStore(store.subscribe, getVersion, getVersion);
  const collector = useContext(CollectorContext);
  let value = key ? (extras.get(key) as T | undefined) : undefined;
  if (key && value === undefined && SSR) {
    value = produce();
    extras.set(key, value);
  }
  if (collector && key && value !== undefined) collector.extra.set(key, value);
  useEffect(() => {
    if (!key || extras.has(key) || extraInflight.has(key)) return;
    const p = fetch(`/data/${key}.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body) => {
        extras.set(key, body);
        store.touch();
      })
      .catch((err) => console.warn('extra data failed:', key, err?.message ?? err))
      .finally(() => extraInflight.delete(key));
    extraInflight.set(key, p);
  }, [key]);
  void version;
  return key ? ((extras.get(key) as T | undefined) ?? null) : null;
};

/** Boot: derived datasets embedded by the prerender. */
export const seedExtras = (data: Record<string, unknown>) => {
  for (const [k, v] of Object.entries(data)) extras.set(k, v);
};

/** A derived dataset by key, if present (list pages: src/data/hub.ts). */
export const extraOf = <T,>(key: string): T | undefined => extras.get(key) as T | undefined;
/** Store a derived dataset and tell subscribers. */
export const putExtra = (key: string, value: unknown) => {
  extras.set(key, value);
  store.touch();
};
/** Edge renderer only: forget every derived dataset between requests (see store.reset). */
export const resetExtras = () => {
  extras.clear();
  extraInflight.clear();
};

const getVersion = () => store.version;

/** Subscribe to the store; request plain slices or a page's backing slice. */
export const useCatalog = (scope: SliceKey[] | Scope = []) => {
  const version = useSyncExternalStore(store.subscribe, getVersion, getVersion);
  const collector = useContext(CollectorContext);
  const isPage = !Array.isArray(scope);
  if (collector && isPage) collector.pages.add(scope.page);
  const wanted = isPage ? `${scope.page}→${scope.backing}` : scope.join('|');
  useEffect(() => {
    if (isPage) void store.ensurePage(scope);
    else for (const k of scope) void store.ensure(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);
  const ready = isPage ? store.pageReady(scope.page) : scope.every((k) => store.has(k));
  return { version, ready };
};

/**
 * `select` runs against whatever the store holds (everything on the server,
 * the page's embedded rows plus any fetched slices in the browser) and
 * re-runs when the store changes. Keep selectors deterministic and based on
 * ranking/dates, so the browser's first pass over the embedded rows picks
 * the same events the server did.
 */
export const useEvents = (
  scope: SliceKey[] | Scope,
  select: (all: Event[]) => Event[],
  deps: unknown[] = [],
  opts: { embed?: 'full' | 'slim' | false } = {}
) => {
  const { version, ready } = useCatalog(scope);
  const collector = useContext(CollectorContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const list = useMemo(() => select(store.all()), [version, ...deps]);
  if (opts.embed !== false) collect(collector, list, opts.embed ?? 'full');
  return { events: list, ready };
};

/** Declare a page key and fetch nothing: true on the server and when the page this browser landed
 *  on embedded the key (its rows are the prerender's or the edge's), false when reached in-app. */
export const usePageKey = (page: string) => {
  const collector = useContext(CollectorContext);
  if (collector) collector.pages.add(page);
  return store.pageReady(page);
};

/** One event by id — full row guaranteed once `ready` (slim rows upgrade).
 *  `loading` is true while the browser is still looking (bucket, then the
 *  edge API), so a page can wait instead of flashing "not found". */
export const useEvent = (id: string | undefined) => {
  const version = useSyncExternalStore(store.subscribe, getVersion, getVersion);
  const collector = useContext(CollectorContext);
  const have = id ? store.get(id) : undefined;
  const wanted = !!id && (!have || !!have.partial);
  useEffect(() => {
    if (id && wanted) void store.ensureEvent(id);
  }, [id, wanted]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const event = useMemo(() => (id ? store.get(id) : undefined), [id, version]);
  if (event) collect(collector, [event], 'event');
  return { event, ready: !!event && !event.partial, loading: !SSR && !!id && !event && !store.settled(id) };
};

/** What the prerender writes into the page for the browser's first render. */
export const serializeCollected = (c: Collector) => {
  const events: RawEvent[] = [];
  const slim: ReturnType<typeof toSlim>[] = [];
  for (const [id, as] of c.rows) {
    const e = store.get(id);
    if (!e) continue;
    if (as === 'slim') slim.push(toSlim(e));
    else events.push(toRaw(e, as === 'event'));
  }
  const venues = [...c.venues].map((slug) => VENUE_PROFILES[slug]).filter(Boolean);
  return { events, slim, pages: [...c.pages], venues, extra: Object.fromEntries(c.extra) };
};

// Only the event page's own row keeps practicalInfo (computed with venue
// facts the browser bundle doesn't carry) and the price-history table
// (`tape`); card rows drop both — ~2.5 KB a card that nothing on a card reads.
const toRaw = (e: Event, withInfo: boolean): RawEvent => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { partial, practicalInfo, tape, ...raw } = e;
  return withInfo ? { ...raw, practicalInfo, ...(tape ? { tape } : {}) } as RawEvent : raw;
};

export const toSlim = (e: Event) =>
  [
    e.id, e.name, e.artist ?? null, e.teams ?? null, e.date, e.time ?? null, e.venue, e.city, e.category,
    e.kind ?? null, e.genre ?? null, e.lowestPrice ?? null, e.lowestAllIn ? 1 : 0, e.imageUrl ?? null,
    e.rank ?? null, e.lat ?? null, e.lng ?? null, e.dayChange ?? null,
    // the venue's country (non-US) and the event's demand: list pages and their filters read them
    e.country && e.country !== 'US' ? e.country : null, e.demand ?? null,
  ] as const;

// Browser boot: the page's own rows, embedded by the prerender — full rows
// for what it rendered, slim rows for what it merely counted — and the page
// keys those rows satisfy.
if (!SSR && typeof window !== 'undefined') {
  const w = window as unknown as {
    __DATA__?: { events?: RawEvent[]; slim?: SlimRow[]; pages?: string[]; venues?: VenueProfile[]; extra?: Record<string, unknown> };
  };
  if (w.__DATA__?.venues?.length) addProfiles(w.__DATA__.venues);
  if (w.__DATA__?.slim?.length) store.add(w.__DATA__.slim.map(fromSlimRow));
  if (w.__DATA__?.events?.length) store.add(w.__DATA__.events.map(toEvent));
  if (w.__DATA__?.pages?.length) store.markPages(w.__DATA__.pages);
  if (w.__DATA__?.extra) seedExtras(w.__DATA__.extra);
}
